<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--titlegroup">
        <span class="a-header--title slide-down-enter-active">Site-Wide Incident</span>
        <HelpButton :content="HELP_CONTENT.siteIncidentDetail" />
      </span>
      <span class="a-header--actions">
        <!-- Closing only, and only while the window is open. There is no
             reopen: a window is a period of time. -->
        <a-popconfirm
          v-if="canClose"
          placement="bottomRight"
          okText="Close the window"
          cancelText="Keep it open"
          :okButtonProps="{ props: { loading: isClosing } }"
          @confirm="closeWindow"
        >
          <template slot="title">
            <p class="mb-1"><b>Close this window now?</b></p>
            <p class="mb-0">The window stops holding alerts back. This cannot be undone.</p>
            <p class="mb-0">A later burst on this website opens a new window.</p>
          </template>
          <a-button size="default" class="a-header--button" :loading="isClosing">
            <a-icon type="check-circle" /> Close window
          </a-button>
        </a-popconfirm>
        <a-button size="default" class="a-header--button" @click="goBack"> <a-icon type="left" /> Back to list </a-button>
      </span>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <div v-if="!isLoading" class="a-body flex-grow-1 overflow-y-auto p-20-px">
      <div class="ce-detail-head">
        <a-tag :color="incident.status === 'active' ? 'red' : 'green'">{{ statusLabel }}</a-tag>
        <span class="ce-detail-head__title">{{ incident.site || '-' }}</span>
        <span class="ce-detail-head__impact">{{ categoryLabel }}</span>
        <SeverityMix :severity="incident.max_severity" :mix="severityMixParts" />
      </div>
      <p class="ce-detail-lead">{{ leadSentence }}</p>

      <a-descriptions bordered :column="2" size="small">
        <a-descriptions-item label="Website">{{ incident.site || '-' }}</a-descriptions-item>
        <a-descriptions-item label="Category">{{ categoryLabel }}</a-descriptions-item>
        <!-- One row, not two. "State" and "Why it closed" said the same thing
             twice: an open window read "Active" and "It is still open." -->
        <a-descriptions-item label="Window State">{{ windowStateLabel }}</a-descriptions-item>
        <a-descriptions-item label="Severity">
          <SeverityMix :severity="incident.max_severity" :mix="severityMixParts" />
        </a-descriptions-item>
        <a-descriptions-item label="Window Opened">
          <template v-if="incident.opened_at">{{ incident.opened_at | dateString }} {{ incident.opened_at | timeString }}</template>
          <template v-else>-</template>
        </a-descriptions-item>
        <a-descriptions-item label="Last Alert in Window">
          <template v-if="incident.last_alert_at">{{ incident.last_alert_at | dateString }} {{ incident.last_alert_at | timeString }}</template>
          <template v-else>-</template>
        </a-descriptions-item>
        <a-descriptions-item label="Window Closed">
          <template v-if="incident.resolved_at">{{ incident.resolved_at | dateString }} {{ incident.resolved_at | timeString }}</template>
          <template v-else>Still open</template>
        </a-descriptions-item>
        <a-descriptions-item label="Window Duration">{{ durationLabel }}</a-descriptions-item>
        <a-descriptions-item label="Alerts">
          {{ formatNumber(totals.serious) }}
          <span v-if="severityBreakdown" class="ce-break">{{ severityBreakdown }}</span>
        </a-descriptions-item>
        <a-descriptions-item label="Pages">{{ formatNumber(totals.seriousPages) }}</a-descriptions-item>
        <a-descriptions-item label="Held back">
          {{ formatNumber(incident.alert_count) }}
          <span class="ce-break">not emailed separately</span>
        </a-descriptions-item>
        <a-descriptions-item label="Groups">{{ formatNumber(incident.event_count) }}</a-descriptions-item>
      </a-descriptions>

      <!-- Timeline. Every row below is read straight from the
           record. Summary messages are not timed here, because the API does not
           yet say when they went out, and a made-up time would be worse than
           none. -->
      <div class="ce-panel">
        <p class="ce-cardtitle">Timeline</p>
        <ul class="ce-tl">
          <li v-for="(row, i) in timelineRows" :key="i" class="ce-tl__row" :class="'ce-tl__row--' + row.kind">
            <span class="ce-tl__t">
              <template v-if="row.at">{{ row.at | dateString }}<br />{{ row.at | timeString }}</template>
              <template v-else>{{ row.whenLabel }}</template>
            </span>
            <span class="ce-tl__dot"><i /></span>
            <span class="ce-tl__what">
              <span class="ce-pill" :class="'ce-pill--' + row.kind">{{ row.pill }}</span>
              {{ row.text }}
            </span>
          </li>
        </ul>
        <p class="ce-caption">
          The summary messages this site-wide incident sends are not timed yet. They are counted from the next phase, and no time is shown here rather
          than a guessed one.
        </p>
      </div>

      <!-- The burst. Hidden entirely when no arrival times came back, because an
           empty chart says nothing. -->
      <div v-if="arrivalBuckets.length" class="ce-panel">
        <p class="ce-cardtitle">Alert Arrivals</p>
        <SeverityToggle
          v-if="arrivalsHaveSeverity"
          :value="visibleSeverities"
          label="Show severity:"
          :counts="arrivalSeverityCounts"
          @change="onSeverityChange"
        />
        <p v-if="arrivalsHaveSeverity && noSeveritySelected" class="ce-empty">No severity is selected, so nothing is drawn.</p>
        <template v-else>
          <div class="ce-spark" role="img" :aria-label="burstAria">
            <span
              v-for="(bucket, i) in arrivalBuckets"
              :key="i"
              class="ce-spark__bar"
              :style="{ height: bucket.height, opacity: bucket.count ? 1 : 0.18 }"
              :title="bucket.hint"
            />
          </div>
          <div class="ce-spark__axis">
            <span>{{ burstStart | dateString }} {{ burstStart | timeString }}</span>
            <span>{{ burstEnd | dateString }} {{ burstEnd | timeString }}</span>
          </div>
        </template>
        <p class="ce-caption">{{ burstCaption }}</p>
      </div>

      <div class="ce-panel">
        <p class="ce-cardtitle">{{ changeEventsHeading }}</p>
        <SeverityToggle
          v-if="changeEvents.length"
          :value="visibleSeverities"
          label="Show severity:"
          :counts="eventSeverityCounts"
          @change="onSeverityChange"
        />
        <template v-if="changeEvents.length">
          <p v-if="noSeveritySelected" class="ce-empty">No severity is selected, so no aggregated alert is shown.</p>
          <p v-else-if="!visibleChangeEvents.length" class="ce-empty">
            No aggregated alert in this site-wide incident has one of the severities you chose.
          </p>
          <div v-for="row in visibleChangeEvents" :key="row.id" class="ce-ev">
            <div class="ce-ev__top">
              <SeverityMix compact :severity="row.severity" :mix="row.severityMix" />
              <router-link class="ce-ev__link" :to="'/change-events/detail/' + row.id">{{ row.summary }}</router-link>
              <span class="ce-ev__count">
                {{ formatNumber(row.alertCount) }} alerts · {{ formatNumber(row.pageCount) }} pages
              </span>
            </div>
            <div class="ce-ev__sub">{{ row.categoryLabel }} · {{ row.changeTypeLabel }}</div>
            <div class="ce-ev__files">
              <span class="ce-ev__fileslabel">What changed:</span>
              <template v-if="row.files.files.length">
                <a-tag v-for="file in row.files.files" :key="file" class="ce-file">{{ file }}</a-tag>
                <span v-if="row.files.more" class="ce-ev__more">and {{ formatNumber(row.files.more) }} more</span>
              </template>
              <span v-else class="ce-ev__more">the file list is not available for this change</span>
            </div>
            <div v-if="row.example" class="ce-ev__example">
              <span class="ce-ev__fileslabel">For example:</span>
              <router-link :to="'/incident/detail/' + row.example.alertId">{{ row.example.name }}</router-link>
              <span v-if="row.example.url" class="ce-ev__url">{{ row.example.url }}</span>
            </div>
          </div>
        </template>
        <p v-else class="ce-empty">No aggregated alerts are recorded against this site-wide incident.</p>
      </div>

      <div class="ce-panel">
        <p class="ce-cardtitle">Alert Trail</p>
        <template v-if="seriousAlerts.length">
          <table class="ce-serious">
            <thead>
              <tr>
                <th class="ce-serious__sev">Severity</th>
                <th>Page</th>
                <th class="ce-serious__when">Detected</th>
                <th class="ce-serious__ev">Alert</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in seriousAlerts" :key="row.key">
                <td class="ce-serious__sev">
                  <SeverityMix compact :severity="row.severity" :mix="null" />
                </td>
                <td>
                  <router-link class="ce-serious__name" :to="'/incident/detail/' + row.alertId">{{ row.name }}</router-link>
                  <!-- The address is stored data. Only http(s) becomes a link;
                       any other scheme is shown as text so it cannot run. -->
                  <a
                    v-if="row.url && row.url !== row.name && isHttpUrl(row.url)"
                    class="ce-serious__url"
                    :href="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ row.url }}</a>
                  <span v-else-if="row.url && row.url !== row.name" class="ce-serious__url">{{ row.url }}</span>
                </td>
                <td class="ce-serious__when">{{ row.detectedTime | dateString }} {{ row.detectedTime | timeString }}</td>
                <td class="ce-serious__ev">
                  <router-link :to="'/incident/detail/' + row.alertId">{{ row.alertId }}</router-link>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="ce-caption">
            Every HIGH and MEDIUM alert in this window, most severe first. Only these two severities open
            a window. LOW alerts are not listed.
          </p>
        </template>
        <p v-else class="ce-empty">
No HIGH or MEDIUM alert is recorded against this site-wide incident yet. This can occur while
          alerts are still awaiting triage.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import { UserDate } from '@shared/services/userDate.service';
  import { isHttpUrl } from '@shared/helpers/utils';
  import ChangeEventsService from '../services/ChangeEvents.service';
  import HelpButton from './HelpButton.vue';
  import severityFilter from '../mixins/severityFilter.mixin';
  import {
    INCIDENT_STATUS_LABELS,
    RESOLVE_REASON_LABELS,
    SEVERITY_STACK_ORDER,
    labelFor,
    severityKey,
    categoryLabel,
    changeTypeLabel,
    severityMix,
    windowState,
    pageName,
    pageUrl,
    fileList,
    formatNumber
  } from '../consts/ChangeEvents.consts';
  import { HELP_CONTENT } from '../consts/helpContent';
  import SeverityMix from './SeverityMix.vue';

  /** How many bars the arrival chart is drawn with. */
  const BURST_BUCKETS = 24;

  /**
   * Reads a time value as milliseconds, or null when it is not a time.
   *
   * A value with no zone is read as UTC, which is what the rest of the app
   * assumes (see `formatUtcTime`). `Date.parse` would read it as browser-local
   * and shift the burst chart by the browser's UTC offset. A value that
   * carries its own offset is honoured.
   * @param {*} value
   * @returns {number|null}
   */
  function toMs(value) {
    if (!value) return null;
    const parsed = moment.utc(String(value).replace(' ', 'T'), moment.ISO_8601);
    return parsed.isValid() ? parsed.valueOf() : null;
  }

  export default {
    name: 'SiteIncidentDetail',
    components: { HelpButton, SeverityMix },
    mixins: [severityFilter],
    data() {
      return {
        HELP_CONTENT,
        isLoading: true,
        isClosing: false,
        incident: {}
      };
    },
    computed: {
      incidentId() {
        return this.$route.params.id;
      },
      categoryLabel() {
        return categoryLabel(this.incident);
      },
      /** The mix of the alerts inside, or null when it was not recounted yet. */
      severityMixParts() {
        return severityMix(this.incident);
      },
      statusLabel() {
        return labelFor(INCIDENT_STATUS_LABELS, this.incident.status);
      },
      /**
       * Whether the Close window button is shown.
       *
       * Only while the window is open. A closed window has nothing to close,
       * and there is no reopen.
       */
      canClose() {
        return this.incident && this.incident.status === 'active';
      },
      /** Window Open, Manually Resolved, or Automatically Closed. */
      windowStateLabel() {
        return windowState(this.incident);
      },
      resolveReasonLabel() {
        if (this.incident.status === 'active') return 'It is still open.';
        return labelFor(RESOLVE_REASON_LABELS, this.incident.resolve_reason);
      },
      leadSentence() {
        const serious = this.formatNumber(this.totals.serious);
        const pages = this.formatNumber(this.totals.seriousPages);
        const mix = this.severityBreakdown ? ` (${this.severityBreakdown})` : '';
        const held = this.formatNumber(this.incident.alert_count);
        const state = this.incident.status === 'active'
          ? 'This window is open.'
          : 'This window is closed.';
        return `${state} ${serious} HIGH or MEDIUM alerts${mix} arrived on ${pages} pages. ${held} of them were not emailed separately, because this window holds them.`;
      },
      /**
       * The alerts this incident holds, counted from the alerts themselves.
       *
       * NOT incident.alert_count, which counts only the alerts held back from
       * being emailed. That number is smaller than the number of alerts the
       * incident contains, so putting it beside a page count made the screen
       * contradict itself - 2 alerts on 7 pages.
       */
      totals() {
        const t = this.incident.alert_totals;
        const zero = {
          serious: 0, seriousPages: 0, high: 0, medium: 0,
          total: 0, pages: 0, low: 0, unscored: 0
        };
        if (!t) return zero;
        return {
          serious: Number(t.serious) || 0,
          seriousPages: Number(t.serious_pages) || 0,
          high: Number(t.high) || 0,
          medium: Number(t.medium) || 0,
          total: Number(t.total) || 0,
          pages: Number(t.pages) || 0,
          low: Number(t.low) || 0,
          unscored: Number(t.unscored) || 0
        };
      },
      /** "2 MEDIUM", or "1 HIGH, 12 MEDIUM, 387 LOW". Empty when nothing counted. */
      severityBreakdown() {
        const parts = [];
        if (this.totals.high) parts.push(`${this.formatNumber(this.totals.high)} HIGH`);
        if (this.totals.medium) parts.push(`${this.formatNumber(this.totals.medium)} MEDIUM`);
        return parts.join(', ');
      },
      /**
       * The HIGH and MEDIUM alerts, worst first.
       *
       * Only these two severities can open a site-wide incident, so these are
       * the alerts that caused it. LOW alerts are left out on purpose.
       */
      seriousAlerts() {
        const rows = Array.isArray(this.incident.serious_alerts) ? this.incident.serious_alerts : [];
        return rows.map((item, index) => ({
          key: item.alert_id == null ? `alert-${index}` : `alert-${item.alert_id}`,
          alertId: item.alert_id,
          severity: item.severity,
          detectedTime: item.detected_time,
          eventId: item.change_event_id,
          name: pageName(item),
          url: pageUrl(item)
        }));
      },
      /** How long the site-wide incident lasted, in plain words. */
      durationLabel() {
        const start = toMs(this.incident.opened_at);
        const end = toMs(this.incident.resolved_at) || toMs(this.incident.last_alert_at);
        if (start == null || end == null || end < start) return '-';
        const minutes = Math.round((end - start) / 60000);
        if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}${rest ? ` ${rest} minutes` : ''}`;
        const days = Math.floor(hours / 24);
        return `${days} day${days === 1 ? '' : 's'} ${hours % 24} hours`;
      },
      /** The arrival times, oldest first, as milliseconds plus their severity. */
      arrivals() {
        const raw = Array.isArray(this.incident.arrivals) ? this.incident.arrivals : [];
        return raw
          .map((item) => {
            if (item && typeof item === 'object') {
              const at = toMs(item.at || item.time || item.detected_time || item.detected_at);
              return at == null ? null : { at, severity: item.severity ? severityKey(item.severity) : null };
            }
            const at = toMs(item);
            return at == null ? null : { at, severity: null };
          })
          .filter(Boolean)
          .sort((a, b) => a.at - b.at);
      },
      /**
       * True only when the API attached a severity to each arrival. It sends
       * plain timestamps today, so the switches are not offered on this chart
       * until they would actually change it.
       */
      arrivalsHaveSeverity() {
        return this.arrivals.length > 0 && this.arrivals.every((a) => a.severity);
      },
      arrivalSeverityCounts() {
        return this.countBySeverity(this.arrivals, (a) => a.severity);
      },
      /** The arrivals actually drawn, after the severity switches. */
      shownArrivals() {
        if (!this.arrivalsHaveSeverity) return this.arrivals;
        return this.arrivals.filter((a) => this.isSeverityOn(a.severity));
      },
      burstStart() {
        const first = this.arrivals.length ? this.arrivals[0].at : null;
        const opened = toMs(this.incident.opened_at);
        const candidates = [first, opened].filter((v) => v != null);
        return candidates.length ? new Date(Math.min.apply(null, candidates)).toISOString() : null;
      },
      burstEnd() {
        const last = this.arrivals.length ? this.arrivals[this.arrivals.length - 1].at : null;
        const closed = toMs(this.incident.resolved_at) || toMs(this.incident.last_alert_at);
        const candidates = [last, closed].filter((v) => v != null);
        return candidates.length ? new Date(Math.max.apply(null, candidates)).toISOString() : null;
      },
      /**
       * The arrival times as bars. One bar for each equal slice of the time the
       * site-wide incident was open, so a burst shows up as a spike.
       */
      arrivalBuckets() {
        if (!this.arrivals.length) return [];
        const t0 = toMs(this.burstStart);
        // A site-wide incident that opened and closed inside a minute would divide by zero.
        const t1 = Math.max(toMs(this.burstEnd), t0 + 60000);
        const span = t1 - t0;
        const counts = new Array(BURST_BUCKETS).fill(0);
        this.shownArrivals.forEach((a) => {
          const i = Math.min(BURST_BUCKETS - 1, Math.max(0, Math.floor(((a.at - t0) / span) * BURST_BUCKETS)));
          counts[i] += 1;
        });
        const peak = Math.max.apply(null, counts.concat([1]));
        const slice = span / BURST_BUCKETS;
        return counts.map((count, i) => {
          const from = new Date(t0 + i * slice);
          const to = new Date(t0 + (i + 1) * slice);
          return {
            count,
            height: `${Math.max(count ? 3 : 2, Math.round((44 * count) / peak))}px`,
            hint: `${count} alert${count === 1 ? '' : 's'} between ${this.clock(from)} and ${this.clock(to)}`
          };
        });
      },
      burstAria() {
        return `When the ${this.formatNumber(this.shownArrivals.length)} alerts of this site-wide incident arrived`;
      },
      burstCaption() {
        const shown = this.formatNumber(this.shownArrivals.length);
        const total = this.formatNumber(this.incident.alert_count);
        const truncated = this.incident.arrivals_truncated
          ? ` The chart draws the first ${shown}. The rest are still counted elsewhere on this page.`
          : '';
        if (!this.arrivalsHaveSeverity) {
          return `Each bar counts the alerts that arrived in one slice of the window. Every alert is drawn, at every severity.${truncated}`;
        }
        return `Each bar counts the alerts that arrived in one slice of the window. Showing ${shown} of ${total} alerts.${truncated}`;
      },
      changeEvents() {
        const rows = Array.isArray(this.incident.change_events) ? this.incident.change_events : [];
        return rows.map((item) => {
          const example = item.example_alert;
          return {
            id: item.id,
            summary: item.summary || `Change on ${item.site || 'a website'}`,
            severity: severityKey(item.severity),
            categoryLabel: categoryLabel(item),
            changeTypeLabel: changeTypeLabel(item),
            severityMix: severityMix(item),
            alertCount: item.alert_count == null ? 0 : item.alert_count,
            pageCount: item.page_count == null ? 0 : item.page_count,
            // "What files caused it": the trigger files if the API named them,
            // otherwise the folders it did name.
            files: fileList(item.trigger_files || item.changed_files || item.top_folders || item.fingerprint),
            example: example
              ? { alertId: example.alert_id, name: pageName(example), url: pageUrl(example) }
              : null
          };
        });
      },
      eventSeverityCounts() {
        return this.countBySeverity(this.changeEvents, (row) => row.severity);
      },
      visibleChangeEvents() {
        return this.changeEvents.filter((row) => this.isSeverityOn(row.severity));
      },
      changeEventsHeading() {
        const count = this.changeEvents.length;
        return `Grouped Alerts in This Window (${this.formatNumber(count)})`;
      },
      /** The pages that produced the most alerts, by name and by address. */
      /**
       * The rows of the timeline, in order. Only what the record can justify —
       * no time is invented.
       */
      timelineRows() {
        const rows = [];
        const opened = toMs(this.incident.opened_at);
        const first = this.arrivals.length ? this.arrivals[0].at : null;

        if (first != null && opened != null && first < opened) {
          rows.push({
            kind: 'send',
            pill: 'sent',
            at: new Date(first).toISOString(),
            text: 'The first alerts were emailed separately, in full.'
          });
        }
        if (this.incident.opened_at) {
          rows.push({
            kind: 'open',
            pill: 'opened',
            at: this.incident.opened_at,
            text: `Window opened. Category: ${this.categoryLabel}.`
          });
        }
        if (this.totals.total > 0) {
          rows.push({
            kind: 'hold',
            pill: 'held',
            at: null,
            whenLabel: 'while open',
            // The raw total stays: it is the only place the LOW and unrated
            // alerts are accounted for, and every other number on the page
            // must add up to it.
            text: `${this.formatNumber(this.totals.total)} alerts arrived on ${this.formatNumber(
              this.totals.pages
            )} pages. ${this.formatNumber(this.totals.serious)} were HIGH or MEDIUM. ${this.formatNumber(
              this.incident.alert_count
            )} were not emailed separately. No alert was deleted.`
          });
        }
        if (this.incident.last_alert_at) {
          rows.push({
            kind: 'send',
            pill: 'last',
            at: this.incident.last_alert_at,
            text: 'The last alert in this window arrived.'
          });
        }
        if (this.incident.resolved_at) {
          rows.push({
            kind: 'close',
            pill: 'closed',
            at: this.incident.resolved_at,
            text: `${this.resolveReasonLabel}. Alerts are emailed separately again.`
          });
        } else {
          rows.push({
            kind: 'open',
            pill: 'open',
            at: null,
            whenLabel: 'now',
            text: 'The window is still open. It closes automatically after the quiet period.'
          });
        }
        return rows;
      }
    },
    mounted() {
      this.fetchDetail();
    },
    methods: {
      /**
       * Close this window by hand.
       *
       * The whole incident is fetched again afterwards rather than patched in
       * place: closing changes the state, the times, the timeline and the lead
       * sentence, and re-reading is the only way to be sure the screen agrees
       * with the record.
       */
      async closeWindow() {
        if (this.isClosing) return;
        this.isClosing = true;
        try {
          await ChangeEventsService.closeSiteIncident(this.incidentId);
          this.$message.success('The window is closed. Alerts from this website are emailed separately again.');
          await this.fetchDetail();
        } catch (err) {
          // 409 means somebody else closed it first, which is not a failure.
          // The screen is simply out of date, so it is re-read.
          const status = err && err.response && err.response.status;
          if (status === 409) {
            this.$message.info('This window was closed already.');
            await this.fetchDetail();
          } else {
            this.$message.error('The window could not be closed. Please try again.');
          }
        } finally {
          this.isClosing = false;
        }
      },
      formatNumber,
      isHttpUrl,
      /**
       * `HH:mm` for a chart hint, in the account time zone, so a bar's hint
       * agrees with the axis under it (which goes through the timeString
       * filter and the same zone).
       */
      clock(date) {
        return moment.tz(date, UserDate.userTimezone).format('HH:mm');
      },
      /**
       * Counts rows by severity, so a severity with nothing in it still gets a
       * switch, drawn faint, instead of quietly disappearing.
       * @param {object[]} rows
       * @param {function} pick reads the severity off one row
       * @returns {Record<string, number>}
       */
      countBySeverity(rows, pick) {
        const counts = {};
        SEVERITY_STACK_ORDER.forEach((key) => {
          counts[key] = 0;
        });
        rows.forEach((row) => {
          const key = severityKey(pick(row));
          counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
      },
      goBack() {
        this.$router.push('/change-events/site-incidents');
      },
      async fetchDetail() {
        try {
          this.incident = (await ChangeEventsService.fetchSiteIncidentDetail(this.incidentId)) || {};
        } catch (err) {
          this.$showMultiErrorMessage(err.messages);
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  // Two buttons where the header expected one.
  .a-header--actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  $card-border: #e3e7ea;
  $ink: #232a31;
  $ink-2: #5b6570;
  $muted: #8a94a0;

  .ce-detail-head {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    &__title {
      font-size: 18px;
      font-weight: 600;
      color: $ink;
    }
    &__impact {
      color: $ink-2;
      font-size: 13px;
    }
  }
  .ce-detail-lead {
    color: $ink-2;
    font-size: 13px;
    margin: 6px 0 16px;
    max-width: 100ch;
  }
  .ce-panel {
    background: #ffffff;
    border: 1px solid $card-border;
    border-radius: 8px;
    padding: 16px 18px;
    margin-top: 14px;
  }
  .ce-cardtitle {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $muted;
    margin: 0 0 10px;
  }
  .ce-caption,
  .ce-empty {
    color: $ink-2;
    font-size: 12.5px;
    margin: 10px 0 0;
    max-width: 100ch;
  }

  // ---- the order things happened
  .ce-tl {
    list-style: none;
    padding: 0;
    margin: 0;

    &__row {
      display: grid;
      grid-template-columns: 120px 20px 1fr;
      gap: 10px;
      padding: 4px 0;

      &--open .ce-tl__dot i {
        background: #ff4d4f;
      }
      &--close .ce-tl__dot i {
        background: #52a54a;
      }
      &--hold .ce-tl__dot i {
        background: #d4d9de;
      }
      &:last-child .ce-tl__dot::after {
        display: none;
      }
    }
    &__t {
      text-align: right;
      color: $muted;
      font-size: 11.5px;
      line-height: 1.35;
    }
    &__dot {
      position: relative;

      i {
        display: block;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        margin: 5px auto 0;
        background: #20a4b8;
      }
      &::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 15px;
        bottom: -6px;
        width: 2px;
        margin-left: -1px;
        background: #edf0f2;
      }
    }
    &__what {
      font-size: 13px;
      color: $ink-2;
      padding-bottom: 6px;
    }
  }
  .ce-pill {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 1px 7px;
    border-radius: 9px;
    margin-right: 6px;
    background: #e8f6f8;
    color: #20a4b8;

    &--open {
      background: #fdeee2;
      color: #e8590c;
    }
    &--close {
      background: #e6f6e9;
      color: #1e8e3e;
    }
    &--hold {
      background: #f0f2f4;
      color: $muted;
    }
  }

  // ---- when the alerts arrived
  .ce-spark {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 48px;
    padding-top: 4px;

    &__bar {
      flex: 1;
      background: #20a4b8;
      border-radius: 2px 2px 0 0;
      min-width: 3px;
    }
    &__axis {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: $muted;
      margin-top: 4px;
    }
  }

  // ---- the aggregated alerts inside
  .ce-ev {
    border: 1px solid $card-border;
    border-radius: 7px;
    padding: 10px 12px;
    margin-bottom: 8px;

    &__top {
      display: flex;
      gap: 8px;
      align-items: baseline;
      flex-wrap: wrap;
    }
    &__link {
      font-weight: 600;
      font-size: 13.5px;
      color: $ink;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: #20a4b8;
        text-decoration: underline;
      }
    }
    &__count {
      margin-left: auto;
      font-size: 12.5px;
      color: $ink-2;
      white-space: nowrap;
    }
    &__sub {
      color: $ink-2;
      font-size: 12px;
      margin: 2px 0 6px;
    }
    &__files,
    &__example {
      font-size: 12px;
      color: $ink-2;
      margin-top: 4px;
    }
    &__fileslabel {
      color: $muted;
      margin-right: 6px;
    }
    &__more {
      color: $muted;
    }
    &__url {
      color: $muted;
      margin-left: 8px;
      word-break: break-all;
    }
  }
  .ce-file.ant-tag {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11px;
  }

  // ---- the alert trail
  .ce-serious {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
      text-align: left;
      font-weight: 600;
      color: #8c8c8c;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      padding: 4px 8px 8px 2px;
      border-bottom: 1px solid #f0f2f4;
      white-space: nowrap;
    }
    td {
      padding: 8px 8px 8px 2px;
      border-bottom: 1px solid #f0f2f4;
      vertical-align: top;
    }
    tr:last-child td {
      border-bottom: none;
    }
    &__sev,
    &__when,
    &__ev {
      white-space: nowrap;
      width: 1%;
    }
    &__name {
      font-weight: 600;
      display: block;
    }
    &__url {
      display: block;
      color: #8c8c8c;
      font-size: 11px;
      word-break: break-all;
    }
  }
  // a plain note beside a number, e.g. "2 MEDIUM"
  .ce-break {
    color: #8c8c8c;
    font-size: 12px;
    margin-left: 6px;
  }

  // ---- kept: the pages list is no longer shown, styles left for reuse
  .ce-pages {
    list-style: none;
    padding: 0;
    margin: 0;

    &__item {
      display: flex;
      align-items: baseline;
      gap: 10px;
      padding: 8px 2px;
      border-bottom: 1px solid #f0f2f4;
      font-size: 13px;

      &:last-child {
        border-bottom: none;
      }
    }
    &__name {
      font-weight: 600;
      color: $ink;
      flex: none;
      max-width: 40%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &__url {
      flex: 1;
      min-width: 0;
      color: $ink-2;
      font-size: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: #20a4b8;
        text-decoration: underline;
      }
    }
    &__count {
      flex: none;
      color: $muted;
      white-space: nowrap;
    }
  }
</style>
